import { setCreditsLanguage } from '@crewdle/ui-kit';
import { Injectable } from '@angular/core';
import { UserService } from 'src/app/services/user/user.service';
import { TranslateService } from '@ngx-translate/core';
import { LoggingChannels, LoggingService } from 'src/app/services/logging/logging.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { FirebaseService } from '../firebase/firebase.service';

function setCrewdleCookie(name: string, value: string): void {
  try {
    const date = new Date();
    date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000);
    document.cookie = `crewdle_${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/;domain=.crewdle.ai;SameSite=Lax`;
  } catch {}
}

function getCrewdleCookie(name: string): string | null {
  try {
    const prefix = `crewdle_${name}=`;
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const trimmed = cookie.trim();
      if (trimmed.startsWith(prefix)) {
        return decodeURIComponent(trimmed.substring(prefix.length));
      }
    }
  } catch {}
  return null;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageChangeService {
  public lang: string;

  constructor(
    private translate: TranslateService,
    private user: UserService,
    private logging: LoggingService,
    private localStorage: LocalStorageService,
    private firebase: FirebaseService,
  ) {
    this.initializeLanguage();
    // The language saved on the user document wins over this device's own copy, so a change on another device
    // arrives here while the app is open. changeLanguage() does not write back to the document.
    this.user.preferences$?.subscribe(({ language }) => {
      const current = this.localStorage.getItem('lang') || this.translate.currentLang || 'en';
      if (language && language !== current) {
        this.changeLanguage(language);
      }
    });
  }

  private initializeLanguage(): void {
    const stored = this.localStorage.getItem('lang');
    const cookie = getCrewdleCookie('lang');

    if (stored) {
      this.lang = stored;
    } else if (cookie) {
      this.lang = cookie;
      this.localStorage.setItem('lang', cookie);
    }
    // Money labels come from the kit and follow the app language.
    setCreditsLanguage(this.lang ?? stored ?? cookie ?? 'en');
  }

  public changeLanguage(lang) {
    this.translate.use(lang);
    setCreditsLanguage(lang);
    this.localStorage.setItem('lang', lang);
    setCrewdleCookie('lang', lang);
  }

  public async authUserChangeLanguage(lang: string) {
    this.changeLanguage(lang);
    // Keep the local user copy in sync immediately. The Firestore write below is
    // async, and checkUserLanguage() (run on navigation) compares
    // user.data.language against localStorage — if the local copy still held the
    // previous value it would revert the language until a page refresh.
    if (this.user.data) {
      this.user.data.language = lang;
    }
    try {
      await this.firebase.globalFirestore.docSet(`users/${this.user.user.uid}`, {
        language: lang,
      }, { merge: true });
    } catch (err) {
      this.logging.log('LanguageService: error to set the user language ', LoggingChannels.Authentication, err);
    }
  }

  public checkUserLanguage() {
    const userLang = this.user.data?.language;
    if (userLang) {
      const appLang = this.localStorage.getItem('lang') || 'en';
      if (userLang !== appLang) {
        this.changeLanguage(userLang);
      }
    }
  }
}
